import React, { Component, createRef } from 'react';
import { connect } from 'react-redux';
import dayjs from 'dayjs';
import { createStructuredSelector } from 'reselect';
import { UPLOAD_SOURCES, getLogger } from '@cld/upload-widget';
import { utils as uploaderUtils } from 'uploader';
import t from 'common/i18n';
import { bindStructuredActions } from 'common/actionsBase';
import { CAMERA_ERRORS } from 'utils/camera';
import TabPanel from 'tabs/hocs/TabPanel';
import Progress from 'root/components/Progress/Progress';
import SvgIcon from 'root/components/SvgIcon';
import Button from 'root/components/Button';
import TabFooter from 'root/components/TabFooter';
import bindRootActions, { types as rootAts } from 'root/actions';
import bindActions, { types as ats } from './actions';
import { selectTabData } from './selectors';
import getCamera from './service';
import SVG_ICONS from '~/assets/icons/icons';

import './CameraTab.scss';

const logger = getLogger();

class CameraTab extends Component {
  static getDerivedStateFromProps(props, state) {
    let newState = null;

    if (!props.tabData.isWidgetShowing && state.snapped) {
      // remove snapped state when widget not showing
      newState = { snapped: false, photo: null };
    }

    return newState;
  }

  state = {
    snapped: false,
    photo: null,
  };

  componentDidMount() {
    if (this.props.tabData.isWidgetShowing) {
      this.loadCamera();
    }
  }

  shouldComponentUpdate(nextProps, nextState) {
    this.snapEffect = nextState.snapped && this.state.snapped !== nextState.snapped;
    return true;
  }

  componentDidUpdate(prevProps) {
    const isShowing = this.props.tabData.isWidgetShowing;
    const wasShowing = prevProps.tabData.isWidgetShowing;

    if (isShowing && !wasShowing) {
      this.loadCamera();
    } else if (wasShowing && !isShowing) {
      this.stopCamera();
    }
  }

  componentWillUnmount() {
    this.stopCamera();
  }

  onActionButtonClicked = () => {
    if (this.cameraApi) {
      const api = this.cameraApi;

      if (!this.state.snapped) {
        // snap a photo from the camera feed
        // first - keep the data from the actual (unscaled) photo
        const photo = api.snapPhoto();
        // second - show (scaled) preview in the UI from the camera stream
        api.snapPreview();

        this.setState({
          snapped: true,
          photo,
        });
      } else {
        // upload the snapped picture
        this.props.addToUploadQueue({
          files: [
            uploaderUtils.createCustomUploadInfo({
              file: this.state.photo,
              name: t('camera.file_name', { time: dayjs().format('YYYYMD_HHmmss') }),
              type: 'image/jpeg',
              camera: true,
            }),
          ],
        });
      }
    }
  };

  onCancelButtonClicked = () => {
    this.setState({ snapped: false });
  };

  getActionText() {
    return !this.state.snapped ? t('camera.capture') : undefined;
  }

  stopCamera() {
    if (this.cameraApi) {
      this.cameraApi.stop();
    }
  }

  videoElmRef = createRef();

  canvasElmRef = createRef();

  cameraApi = null;

  snapEffect = null;

  loadCamera() {
    this.props.actions[ats.SET_CAMERA_LOADING]({ loading: true });

    const videoElm = this.videoElmRef.current;
    const canvasElm = this.canvasElmRef.current;

    if (videoElm && canvasElm) {
      getCamera({
        videoElm,
        canvasElm,
      })
        .then((cameraApi) => {
          this.props.actions[ats.SET_USER_ALLOWED_CAMERA]();
          this.cameraApi = cameraApi;
        })
        .catch((error) => {
          logger.log('[CameraTab]: Camera usage refused !', error);

          this.props.actions[ats.SET_CAMERA_NOT_FOUND]({ status: true });

          if (error.reason !== CAMERA_ERRORS.PermissionDeniedError) {
            this.props.rootActions[rootAts.SET_ERROR]({
              text: t('camera.camera_error'),
            });
          }
        });
    }
  }

  retryCamera = () => {
    this.loadCamera();
  };

  renderNotAllowed() {
    return (
      <div className="not-allowed-container tc">
        <strong className="title">{t('camera.take_pic')}</strong>
        <SvgIcon path={SVG_ICONS.CAMERA_INFO} size={72} colorName="window-border" className="ma5" />
        <p>{t('camera.explanation')}</p>
        <div className="not-found-container">
          <Button colorName="link" text={t('camera.retry')} onClick={this.retryCamera} />
        </div>
      </div>
    );
  }

  renderAllowed() {
    return (
      <div className={`allowed-container ${this.state.snapped ? 'snapped' : ''}`}>
        <video id="camera-video" ref={this.videoElmRef} />
        <div className={`canvas-holder ${this.snapEffect ? 'snap-action' : ''}`}>
          <canvas id="camera-canvas" ref={this.canvasElmRef} />
        </div>
      </div>
    );
  }

  render() {
    const progressIndicator = this.props.tabData.isLoading ? <Progress size={42} overlay /> : null;

    return (
      <div
        className={`bg-source-bg bg-source-bg-c content-wrap camera-block
            ${this.props.tabData.isAllowed ? 'camera-allowed' : ''}
            ${this.props.tabData.notFound ? 'camera-not-found' : ''}`}
      >
        <div className="holder large-thin-menu-holder pb6 relative">
          {progressIndicator}
          <div className="box centered">
            {this.renderNotAllowed()}
            {this.renderAllowed()}
          </div>
        </div>

        <TabFooter
          actionButtonActive
          showActionButton={this.props.tabData.isAllowed}
          actionButtonText={this.getActionText()}
          uploadButtonHandler={this.onActionButtonClicked}
          showSecondaryButton={this.state.snapped}
          secondaryButtonText={t('camera.cancel')}
          secondaryButtonHandler={this.onCancelButtonClicked}
        />
      </div>
    );
  }
}

export default TabPanel({
  id: UPLOAD_SOURCES.CAMERA,
})(
  connect(
    createStructuredSelector({
      tabData: selectTabData,
    }),
    bindStructuredActions({
      actions: bindActions,
      rootActions: bindRootActions,
    })
  )(CameraTab)
);
